/**
 * Connector capability endpoints — catalog, MyConnector, AdminConnector,
 * install/toggle/disconnect/request-access, vote, dashboard, OAuth URL builder.
 */

import { ApiError, extractError, get, handlePatchResponse, post } from '../_shared.ts';

export interface MyConnector {
  instance_id: string;
  template_id: string;
  template_name: string;
  display_name: string;
  description: string | null;
  enabled: boolean;
}

export interface DashboardData {
  my_connectors: MyConnector[];
  usage_this_cycle: { dispatch_count: number; total_cost_units: number };
  recent_activity: Array<{
    id: string;
    /** Push 4-1d-k-cont Class B (2026-07-02): sys__event_log.short_id
     *  (evt-prefix) for Home.tsx RecordLink → /activity/:short_id. */
    short_id: string;
    connector_name: string;
    status: string;
    duration_ms: number | null;
    created_at: string;
  }>;
  knowledge_count: number;
}

export function fetchMyDashboard(): Promise<DashboardData> {
  return get<DashboardData>('/api/me/dashboard');
}

export type ConnectorMode = 'custom' | 'proxy';

/**
 * Push #91 (Ean dogfood 2026-06-22) — D-088 OAuth start URL routing.
 *
 * Pattern A (`custom` = hand-rolled Olytic microservice — ga4, google_ads,
 * linkedin_ads, etc.) uses `/oauth/<vendor>/start` which is the per-vendor
 * Pages Function at `apps/ui/functions/oauth/[vendor]/start.ts`.
 *
 * Pattern B (`proxy` = upstream MCP proxy — supabase today; future vendor
 * MCPs we don't hand-roll) uses `/oauth/upstream/start` which is the
 * D-116 chained_oauth handler at `apps/ui/functions/oauth/upstream/start.ts`.
 * That endpoint looks up the connector_instance_id to derive the vendor;
 * no `<vendor>` segment in the URL because proxy mode IS the vendor-agnostic
 * path.
 *
 * Bug it fixes: SPA inlined `/oauth/${template_name}/start` everywhere, so
 * supabase → `/oauth/supabase/start` hit the per-vendor handler which
 * doesn't know about supabase and returned `unknown_vendor:supabase`. The
 * SPA's auto-redirect then looped through /sign-in indefinitely (Push #91
 * SignIn fix broke the loop, but the underlying URL routing was still
 * wrong). This helper centralizes the routing decision; callers pass
 * connector_mode + template_name and get the right URL back.
 */
export function buildConnectorOauthStartUrl(args: {
  templateName: string;
  connectorMode: ConnectorMode;
  instanceId: string;
  returnTo?: 'spa' | 'mcp';
}): string {
  const returnTo = args.returnTo ?? 'spa';
  const params = new URLSearchParams({
    connector_instance_id: args.instanceId,
    return_to: returnTo,
  });
  if (args.connectorMode === 'proxy') {
    // Pattern B — upstream MCP proxy. Vendor is derived server-side from
    // the connector_instance_id row.
    return `/oauth/upstream/start?${params.toString()}`;
  }
  // Pattern A — hand-rolled per-vendor microservice. Vendor slug is in
  // the URL because the per-vendor Pages Function dispatches on it.
  return `/oauth/${args.templateName}/start?${params.toString()}`;
}

export interface CatalogTemplate {
  id: string;
  /** Phase 2 Block 2A — connector_template.short_id (cnt-prefix). */
  short_id: string;
  name: string;
  display_name: string;
  description: string | null;
  version: string;
  is_installed: boolean;
  is_mine: boolean;
  is_preview: boolean;
  /** D-088 */
  connector_mode: ConnectorMode;
  /** D-088, absolute URL or null if not yet branded */
  logo_url: string | null;
  /** D-088, admin-surfaced footnote, typically explains SLA inheritance for proxy mode */
  sla_disclosure: string | null;
}

export function fetchCatalog(): Promise<{ templates: CatalogTemplate[] }> {
  return get('/api/catalog');
}

export interface CatalogToolEntry {
  id: string;
  /** Phase 2 Block 2A — tool_instance.short_id when the caller has installed
   *  the instance; null if just template-level (caller hasn't installed yet).
   *  Used by ToolsSection to route /tools/:short_id flat URLs. */
  tool_instance_short_id?: string | null;
  name: string;
  display_name: string;
  description: string | null;
  when_to_use: string | null;
  operation_verb: string | null;
  cost_units: number;
  /** D-093 — 'read' | 'write' | 'destructive'. Lets the UI group tools by
   *  scope so a user/admin can reason about access at a class level. May be
   *  absent on older catalog endpoints; defaults to 'read' in render code. */
  access_class?: 'read' | 'write' | 'destructive';
}

export interface CatalogDetail {
  template: {
    id: string;
    /** Phase 2 Block 2A — connector_template.short_id (cnt-prefix). */
    short_id: string;
    name: string;
    display_name: string;
    description: string | null;
    version: string;
    oauth_config: unknown;
    is_installed: boolean;
    is_mine: boolean;
    is_preview: boolean;
    connector_mode: ConnectorMode;
    logo_url: string | null;
    sla_disclosure: string | null;
    upstream_mcp_url: string | null;
    instance_id: string | null;
    /** Phase 2 Block 2A — the caller's own assigned instance's short_id (cni-
     *  prefix), if any. Closes the LibraryDetail market→instance navigation
     *  bug: "Open" button now routes to the caller's specific multi-instance
     *  row instead of template.name (which is ambiguous across instances). */
    instance_short_id: string | null;
  };
  tools: CatalogToolEntry[];
}

// Push 4-1d-m (2026-07-06): market catalog detail moved to catalog.ts as
// fetchCatalogConnectorDetail. The kind-specific endpoint lives at
// /api/catalog/connectors/:short_id. CatalogDetail interface stays here
// (colocated with connector-instance types); catalog.ts imports it.
// Push 73 (2026-07-17): kind-agnostic fetchCatalogDetail alias deleted after
// LibraryDetail + AdminMarketDetail migrated to fetchCatalogConnectorDetail.

export interface MyConnectorDetailed {
  instance_id: string;
  /** Phase 2 Block 2A — connector_instance.short_id (cni-prefix) for
   *  list-row → /connectors/:short_id deep-link. */
  short_id: string;
  template_id: string;
  template_name: string;
  display_name: string;
  description: string | null;
  version: string;
  enabled: boolean;
  tool_count: number;
  oauth_status: 'connected' | 'needs_reauth' | 'not_authorized' | string;
  /** D-088 */
  connector_mode: ConnectorMode;
  /** D-088 */
  logo_url: string | null;
  /** D-088 */
  sla_disclosure: string | null;
  /** Sub-agent MMMMM Item B (2026-07-29) — server-computed OAuth start URL.
   *  Non-null when the SPA should surface a "Connect" chip inline (i.e.
   *  oauth_status is 'not_authorized' or 'needs_reauth' AND the template
   *  is Pattern A or B). Null when already authorized, or Pattern C
   *  (upstream_auth_mode='none' — self-contained MCP proxy where the
   *  third-party handles upstream auth at their layer). */
  oauth_start_url?: string | null;
}

export function fetchMyConnectors(): Promise<{ connectors: MyConnectorDetailed[] }> {
  return get<{ connectors: MyConnectorDetailed[] }>('/api/me/connectors');
}

// ---------------------------------------------------------------------
// Admin connector instance detail (D-092/D-094/D-098 Lightning record page)
// ---------------------------------------------------------------------

export interface AdminToolInstance {
  tool_instance_id: string;
  /** Phase 2 Block 2A — tool_instance.short_id (tli-prefix). Used by
   *  AdminConnectorDetail's tool-row PATCH callsites which now target the
   *  flat /api/admin/tools/:short_id endpoint. */
  tool_instance_short_id: string;
  tool_template_id: string;
  name: string;
  display_name: string;
  display_name_template: string;
  description: string | null;
  description_template: string | null;
  when_to_use: string | null;
  when_to_use_template: string | null;
  operation_verb: string;
  access_class: 'read' | 'write' | 'destructive';
  /**
   * Per Migration 0116 (Push #4.5 Item 1): 2-value enum (allow / disallow).
   * 'ask' kept in the union for backward compat with any unsynced caller;
   * new DB rows can only carry the 2 server-enforced values.
   */
  permission_mode: 'allow' | 'disallow';
  /** Effective cost (override if set, template default otherwise). */
  cost_units: string;
  cost_units_template: string;
  cost_units_override: string | null;
  enabled: boolean;
  vendor_extras: unknown;
  description_override: string | null;
  display_name_override: string | null;
  when_to_use_override: string | null;
  usage_guidance: string | null;
}

export interface AdminToolInstancePatch {
  enabled?: boolean;
  /**
   * Per Migration 0116 (Push #4.5 Item 1): 2-value enum (allow / disallow).
   * Server-side CHECK constraint rejects 'ask' + 'audit'.
   */
  permission_mode?: 'allow' | 'disallow';
  display_name_override?: string | null;
  description_override?: string | null;
  when_to_use_override?: string | null;
  cost_units_override?: number | null;
  vendor_extras?: Record<string, unknown> | null;
  usage_guidance?: string | null;
}

/**
 * Phase 2 Block 2A (2026-06-30) — URL flattened from
 *   /api/admin/connectors/instance/:instance_id/tools/:tool_id
 *     →
 *   /api/admin/tools/:short_id
 * Caller-side: pass the tool_instance.short_id (tli-prefix). The legacy
 * instanceId positional arg is gone — server resolves the connector parent
 * via JOIN.
 */
export function updateAdminToolInstance(
  tool_short_id: string,
  patch: AdminToolInstancePatch,
): Promise<{ ok: true; changed: number }> {
  return fetch(
    `/api/admin/tools/${encodeURIComponent(tool_short_id)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  ).then(async (res) => {
    const text = await res.text();
    const parsed: unknown = text === '' ? null : (JSON.parse(text) as unknown);
    if (!res.ok) {
      const msg =
        parsed !== null && typeof parsed === 'object' && 'message' in parsed
          ? String((parsed as { message: unknown }).message)
          : `PATCH tool ${tool_short_id} failed (${res.status})`;
      throw new ApiError(res.status, msg);
    }
    return parsed as { ok: true; changed: number };
  });
}

export interface AdminInstanceDetail {
  instance: {
    instance_id: string;
    /** Phase 2 Block 2A — connector_instance.short_id (cni-prefix) */
    short_id: string;
    template_id: string;
    /** Phase 2 Block 2A — connector_template.short_id (cnt-prefix) for
     *  /market/:short_id deep-link from admin record page */
    template_short_id: string;
    template_name: string;
    template_display_name: string;
    display_name: string;
    description: string | null;
    usage_guidance: string | null;
    enabled: boolean;
    connector_mode: ConnectorMode;
    logo_url: string | null;
    sla_disclosure: string | null;
    upstream_mcp_url: string | null;
    oauth_status: 'connected' | 'needs_reauth' | 'not_authorized' | string;
  };
  tools: AdminToolInstance[];
  assigned_users: Array<{
    account_user_id: string;
    /** Push 4-1e-cleanup-fu2 (2026-07-08): sys__account_user.short_id
     *  (usr-prefix) so the assigned-users list can drill via canonical
     *  /admin/people/:short_id URL. */
    account_user_short_id: string;
    display_name: string;
    email: string;
    role: string;
    is_active: boolean;
  }>;
}

/**
 * Phase 2 Block 2A (2026-06-30) — URL flattened from
 *   /api/admin/connectors/instance/:instance_id  →  /api/admin/connectors/:short_id
 * Caller-side: pass the connector_instance.short_id (cni-prefix).
 */
export function fetchAdminInstanceDetail(short_id: string): Promise<AdminInstanceDetail> {
  return get<AdminInstanceDetail>(
    `/api/admin/connectors/${encodeURIComponent(short_id)}`,
  );
}

/** Phase 2 Block 2A — URL flattened; param is connector_instance.short_id. */
export function patchAdminInstance(
  short_id: string,
  patch: {
    display_name?: string;
    description_override?: string;
    usage_guidance?: string;
    enabled?: boolean;
  },
): Promise<{ ok: true; instance_id: string }> {
  return fetch(`/api/admin/connectors/${encodeURIComponent(short_id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }).then(handlePatchResponse) as Promise<{ ok: true; instance_id: string }>;
}

/**
 * Push 4-1g (2026-07-08): Clone a connector instance. Deep-copies the
 * source row + its child tool_instance rows under a new short_id + a
 * new display_name; fresh clones start unassigned + require re-auth
 * via the standard per-user OAuth bridge. Endpoint returns 501 until
 * the clone machinery lands.
 */
export async function cloneAdminConnectorInstance(
  short_id: string,
): Promise<{ ok: true; short_id: string }> {
  const path = `/api/admin/connectors/${encodeURIComponent(short_id)}/clone`;
  const res = await fetch(path, { method: 'POST', credentials: 'include' });
  const text = await res.text();
  if (!res.ok) {
    const errInfo = extractError(text, path, 'POST', res.status);
    throw new ApiError(res.status, errInfo.message, errInfo.detail, errInfo.incidentId, errInfo.code);
  }
  return JSON.parse(text) as { ok: true; short_id: string };
}

/**
 * Push 4-1g (2026-07-08): Uninstall a connector instance. Removes the
 * connector from the workspace + cascades assignment junction rows +
 * revokes per-user OAuth tokens at the Secret Manager layer. Endpoint
 * returns 501 until the uninstall machinery lands.
 */
export async function uninstallAdminConnectorInstance(
  short_id: string,
): Promise<{ ok: true }> {
  const path = `/api/admin/connectors/${encodeURIComponent(short_id)}/uninstall`;
  const res = await fetch(path, { method: 'POST', credentials: 'include' });
  const text = await res.text();
  if (!res.ok) {
    const errInfo = extractError(text, path, 'POST', res.status);
    throw new ApiError(res.status, errInfo.message, errInfo.detail, errInfo.incidentId, errInfo.code);
  }
  return JSON.parse(text) as { ok: true };
}

/**
 * Phase 2 Block 2A (2026-06-30) — URL flattened from
 *   /api/admin/connectors/instance/:instance_id/tools/:tool_id
 *     →
 *   /api/admin/tools/:short_id
 * Caller-side: pass the tool_instance.short_id (tli-prefix).
 */
export function patchToolInstance(
  tool_short_id: string,
  patch: {
    /**
     * Per Migration 0116 (Push #4.5 Item 1): permission_mode is a 2-value
     * enum (allow / disallow). 'ask' is retained in the type union for
     * backward compat with any in-flight call site, but the DB CHECK
     * constraint rejects it. New callsites should use only 'allow' or
     * 'disallow'. The PermissionPicklist UI surfaces only those two.
     */
    permission_mode?: 'allow' | 'disallow';
    usage_guidance?: string;
    description_override?: string;
    display_name_override?: string;
    when_to_use_override?: string;
  },
): Promise<{ ok: true; tool_instance_id: string; permission_mode: string }> {
  return fetch(`/api/admin/tools/${encodeURIComponent(tool_short_id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }).then(handlePatchResponse) as Promise<{
    ok: true;
    tool_instance_id: string;
    permission_mode: string;
  }>;
}

/**
 * Push #4.5 Item 6 — bulk set permission_mode for every tool in a
 * connector_instance matching a given access_class. Convenience affordance
 * for the per-section "Set all [Read/Write/Destructive] tools to: [Allow ▾]"
 * picker on AdminConnectorDetail.
 */
export function bulkSetToolPermission(
  connectorInstanceId: string,
  accessClass: 'read' | 'write' | 'destructive',
  permissionMode: 'allow' | 'disallow',
): Promise<{ ok: true; changed: number }> {
  return fetch('/api/admin/tool-instance/bulk-permission', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      connector_instance_id: connectorInstanceId,
      access_class: accessClass,
      permission_mode: permissionMode,
    }),
  }).then(async (res) => {
    const text = await res.text();
    const parsed: unknown = text === '' ? null : (JSON.parse(text) as unknown);
    if (!res.ok) {
      const msg =
        parsed !== null && typeof parsed === 'object' && 'message' in parsed
          ? String((parsed as { message: unknown }).message)
          : `POST /api/admin/tool-instance/bulk-permission failed (${res.status})`;
      throw new ApiError(res.status, msg);
    }
    return parsed as { ok: true; changed: number };
  });
}

export interface MyConnectorDetail {
  instance: {
    instance_id: string;
    /** Phase 2 Block 2A — connector_instance.short_id (cni-prefix). */
    short_id: string;
    template_id: string;
    /** Push 4-1af (2026-07-10): mcp__connector_template.short_id (cnt-prefix)
     *  for the "View in market" action → /market/connectors/:template_short_id. */
    template_short_id: string;
    template_name: string;
    display_name: string;
    description: string | null;
    /** Admin-set "How your team uses this" copy. Surfaces prominently in user UI. */
    usage_guidance: string | null;
    version: string;
    enabled: boolean;
    oauth_status: string;
    /** D-088 */
    connector_mode: ConnectorMode;
    /** D-088 */
    logo_url: string | null;
    /** D-088 */
    sla_disclosure: string | null;
  };
  tools: CatalogToolEntry[];
  recent_activity: Array<{
    id: string;
    status: string;
    duration_ms: number | null;
    vendor_http_code: number | null;
    created_at: string;
  }>;
}

/**
 * Phase 2 Block 2A (2026-06-30) — param renamed from `slug` to `short_id`.
 * Caller-side: pass the connector_instance.short_id (cni-prefix). Server-side:
 * WHERE i.short_id = $3. Closes multi-instance-shared-slug collision.
 */
export function fetchMyConnectorDetail(short_id: string): Promise<MyConnectorDetail> {
  return get<MyConnectorDetail>(`/api/me/connectors/${encodeURIComponent(short_id)}`);
}

// ---------------------------------------------------------------------
// User-facing Tool record (Phase 2 Block 2A — flat URL /tools/:short_id)
// ---------------------------------------------------------------------

/**
 * Shape returned by GET /api/me/tools/:short_id. The :short_id is the
 * tool_instance.short_id (tli-prefix). Server-side JOIN resolves the parent
 * connector context + filters activity to this tool only (via
 * primitive_instance_id = tool_instance.id).
 */
export interface MyToolDetail {
  tool: {
    /** tool_template.id — kept as `id` for CatalogToolEntry compat */
    id: string;
    tool_instance_short_id: string;
    name: string;
    display_name: string;
    description: string | null;
    when_to_use: string | null;
    operation_verb: string | null;
    cost_units: number;
    access_class: 'read' | 'write' | 'destructive';
  };
  connector: {
    /** connector_instance.short_id (cni-prefix) — for /connectors/:short_id back-link */
    short_id: string;
    instance_id: string;
    display_name: string;
    template_name: string;
    template_id: string;
    connector_mode: ConnectorMode;
    logo_url: string | null;
    oauth_status: string;
  };
  recent_activity: Array<{
    id: string;
    status: string;
    duration_ms: number | null;
    vendor_http_code: number | null;
    created_at: string;
  }>;
}

/**
 * Phase 2 Block 2A — flat URL /tools/:short_id. Pass the tool_instance.short_id
 * (tli-prefix); server resolves parent connector via JOIN.
 */
export function fetchMyToolDetail(short_id: string): Promise<MyToolDetail> {
  return get<MyToolDetail>(`/api/me/tools/${encodeURIComponent(short_id)}`);
}

/**
 * User-mode mirror of admin's revoke-authorization. Flips the calling user's
 * mcp__user_connector_authorization row to needs_reauth=true for this
 * connector. The token in GCP Secret Manager is left intact — the next OAuth
 * callback UPSERT overwrites it cleanly. Reversible: user can click Reconnect
 * immediately after.
 *
 * Phase 2 Block 2A — param renamed from slug to short_id (cni-prefix).
 */
export function disconnectMyConnector(short_id: string): Promise<{ ok: boolean }> {
  return post<{ ok: boolean }>(
    `/api/me/connectors/${encodeURIComponent(short_id)}/disconnect`,
    {},
  );
}

export function requestConnectorAccess(
  templateName: string,
  note?: string,
): Promise<{ ok: true; event_id: string; already_requested: boolean }> {
  return post('/api/me/request-access', {
    template_name: templateName,
    ...(note !== undefined && note !== '' ? { note } : {}),
  });
}

/**
 * Catalog upvote signal. Migration H (2026-06-24): refactored to write
 * directly to the template row's `upvote_count` column. Per-user
 * idempotence tracked via sys__account_user.ui_preferences.upvoted_slugs;
 * the server returns the authoritative upvote_count + already_upvoted
 * flag so optimistic UI can reconcile.
 *
 * @param requestType   'connector' | 'knowledge' | 'model' (pillar)
 * @param targetSlug    The template slug being upvoted (connector/knowledge:
 *                      template.name; model: template.model_id)
 * @returns ok + already_upvoted (true if user had previously upvoted) +
 *          upvote_count (authoritative post-write count from server)
 */
export interface VoteResponse {
  ok: true;
  already_upvoted: boolean;
  upvote_count: number;
}

export function castVote(
  requestType: 'connector' | 'knowledge' | 'model',
  targetSlug: string,
): Promise<VoteResponse> {
  return post('/api/me/vote', {
    request_type: requestType,
    target_slug: targetSlug,
  });
}

export interface AdminConnectorInstance {
  instance_id: string;
  /** Phase 2 Block 2A — connector_instance.short_id (cni-prefix) for
   *  list-row → /admin/connectors/:short_id deep-link. */
  short_id: string;
  template_id: string;
  template_name: string;
  template_display_name: string;
  display_name: string;
  description: string | null;
  version: string;
  enabled: boolean;
  assigned_user_count: number;
  dispatches_this_cycle: number;
  created_at: string;
  /** D-088 */
  connector_mode: ConnectorMode;
  /** D-088 */
  logo_url: string | null;
  /** D-088 */
  sla_disclosure: string | null;
}

export function fetchAdminConnectors(): Promise<{
  instances: AdminConnectorInstance[];
}> {
  return get('/api/admin/connectors');
}

export function installConnector(
  templateName: string,
): Promise<{ ok: true; instance_id: string }> {
  return post('/api/admin/connectors/install', { template_name: templateName });
}

export function toggleConnectorEnabled(
  instanceId: string,
  enabled: boolean,
): Promise<{ ok: true }> {
  return post('/api/admin/connectors/toggle', {
    instance_id: instanceId,
    enabled,
  });
}

/**
 * Push 4-1l (2026-07-09) — power-admin per-account override for a connector
 * instance. Passes an optional subset of override columns. Empty body ⇒ 400.
 */
export interface ConnectorOverrideBody {
  display_name?: string;
  description_override?: string | null;
  when_to_use_override?: string | null;
  usage_guidance?: string | null;
  microservice_url_override?: string | null;
}
export function overrideAdminConnectorInstance(
  short_id: string,
  body: ConnectorOverrideBody,
): Promise<{ ok: true; instance_id: string }> {
  return post(
    `/api/admin/connectors/${encodeURIComponent(short_id)}/override`,
    body,
  );
}
